import { createBrowserClient } from '@supabase/ssr';
import { createClient as createSupabaseJsClient, type SupabaseClient } from '@supabase/supabase-js';
import { FLAGS } from '@/config/featureFlags';
import { createPreferencesStorage } from '@/lib/native/preferencesStorage';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Browser client with cookie-based session storage.
 *
 * Previously used localStorage. Now stores tokens in cookies with Secure +
 * SameSite=Strict so they are not sent cross-origin (CSRF reduction).
 * These cookies are still JavaScript-readable — createBrowserClient cannot
 * set HttpOnly — so XSS can still read the session. Treat XSS as a session
 * compromise and keep the CSP tight.
 *
 * @supabase/ssr's createBrowserClient handles:
 * - PKCE flow for OAuth
 * - Automatic token refresh
 * - Cookie chunking for large sessions
 * - onAuthStateChange events (same as before)
 *
 * Env-less checkouts (fresh worktree without .env): createBrowserClient
 * THROWS on empty url/key at module import, which white-screens the app and
 * crashes vitest collection (Vidi-ali). So the client is only created when
 * both vars are non-empty; otherwise a lazy proxy surfaces the documented
 * warning on first property access instead of dying at import. Prod (Vercel)
 * always has the vars set.
 */
function createClient(): SupabaseClient {
  if (FLAGS.nativeIOS) {
    // Native (plan §2.2): the cookie client is unusable inside the shell —
    // `location.origin` is `capacitor://localhost` and iOS will not return a
    // `Secure` cookie to it, so the session would be lost on every launch.
    // `auth.storage` cannot be passed to createBrowserClient as a fix either:
    // @supabase/ssr spreads `...options?.auth` and then overwrites `storage`
    // with its own cookie adapter (createBrowserClient.js:35-44), so the value
    // is silently discarded. Hence the plain supabase-js client here.
    //
    // The export stays SYNCHRONOUS on purpose. The plan sketched an async
    // export, but GoTrue accepts an async storage adapter (auth-js types
    // `storage` as `SupportedStorage`, whose methods are `MaybePromisify`),
    // so the Preferences round trip can hide inside the adapter instead of
    // rippling through 19 importers. That refactor belongs to Vidi-d28.
    //
    // detectSessionInUrl: false — there is no browser address bar to read an
    // OAuth fragment from; the shell hands the callback in explicitly.
    // No module is added to the web bundle by this branch: `createClient` from
    // @supabase/supabase-js is already in the graph via @supabase/ssr, and the
    // dynamic `import('@capacitor/preferences')` sits under `FLAGS.nativeIOS`,
    // the literal `false` on web, so Rollup deletes it (N1).
    return createSupabaseJsClient(supabaseUrl as string, supabaseAnonKey as string, {
      auth: {
        flowType: 'pkce',
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        storage: createPreferencesStorage(() => import('@capacitor/preferences')),
      },
    });
  }
  return createBrowserClient(supabaseUrl as string, supabaseAnonKey as string, {
    cookieOptions: {
      secure: true,
      sameSite: 'strict',
      path: '/',
    },
  });
}

export const supabase: SupabaseClient =
  supabaseUrl && supabaseAnonKey
    ? createClient()
    : (new Proxy({} as SupabaseClient, {
        get(_target, prop) {
          // Missing-.env worktree/dev signal, not a production exception —
          // reportError would spam Sentry from local checkouts (Vidi-02m).
          console.warn('Supabase env vars not set. Auth will not work.');
          throw new Error(
            `Supabase client used without env vars (accessed ${String(prop)}). ` +
              'Copy .env into this checkout/worktree.',
          );
        },
      }) as SupabaseClient);
